import {
  createContext,
  useContext,
  useEffect,
  useState,
  ReactNode,
  useCallback,
} from "react";
import * as authApi from "@/api/auth";
import { getAccessToken, getStoredUser } from "@/api/client";
import type { Session, User } from "@/types/auth";

interface AuthContextType {
  user: User | null;
  session: Session | null;
  signUp: (
    email: string,
    password: string,
  ) => Promise<{ error: Error | null; needsVerification?: boolean }>;
  signIn: (email: string, password: string) => Promise<{ error: Error | null }>;
  signOut: () => Promise<void>;
  loading: boolean;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

function toLegacyUser(userId: string, email: string): User {
  return { id: userId, email };
}

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<User | null>(null);
  const [session, setSession] = useState<Session | null>(null);
  const [loading, setLoading] = useState(true);

  const applyTokens = useCallback(
    (tokens: {
      accessToken: string;
      refreshToken: string;
      userId: string;
      email: string;
    }) => {
      const legacyUser = toLegacyUser(tokens.userId, tokens.email);
      setUser(legacyUser);
      setSession({
        access_token: tokens.accessToken,
        refresh_token: tokens.refreshToken,
        user: legacyUser,
      });
    },
    [],
  );

  useEffect(() => {
    let cancelled = false;

    async function hydrate() {
      const stored = getStoredUser();
      const access = getAccessToken();
      if (!stored || !access) {
        if (!cancelled) setLoading(false);
        return;
      }

      // Optimistic hydrate so Analyse / upload aren't blocked while /auth/me runs.
      if (!cancelled) {
        applyTokens({
          accessToken: access,
          refreshToken: localStorage.getItem("rubby-front-refresh-token") ?? "",
          userId: stored.userId,
          email: stored.email,
        });
      }

      try {
        const me = await authApi.getMe();
        if (cancelled) return;
        applyTokens({
          accessToken: access,
          refreshToken: localStorage.getItem("rubby-front-refresh-token") ?? "",
          userId: me.userId,
          email: me.email,
        });
      } catch {
        if (!cancelled) {
          authApi.logout();
          setUser(null);
          setSession(null);
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    }

    void hydrate();
    return () => {
      cancelled = true;
    };
  }, [applyTokens]);

  const signUp = async (email: string, password: string) => {
    try {
      await authApi.register({ email, password, locale: "fa" });
      const tokens = await authApi.login(email, password);
      applyTokens(tokens);
      return { error: null as Error | null, needsVerification: false };
    } catch (err) {
      return { error: err instanceof Error ? err : new Error(String(err)) };
    }
  };

  const signIn = async (email: string, password: string) => {
    try {
      const tokens = await authApi.login(email, password);
      applyTokens(tokens);
      return { error: null as Error | null };
    } catch (err) {
      return { error: err instanceof Error ? err : new Error(String(err)) };
    }
  };

  const signOut = async () => {
    authApi.logout();
    setUser(null);
    setSession(null);
  };

  return (
    <AuthContext.Provider
      value={{ user, session, signUp, signIn, signOut, loading }}
    >
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error("useAuth must be used within an AuthProvider");
  }
  return context;
}
